import { useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Customized,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  AgentApiAnalysisSharedChart,
  AgentApiAnalysisSharedChartSeries,
  AgentApiAnalysisSharedSummaryItem,
} from '@/types/serializers';
import { colorForSeries, OTHERS_GROUP_KEY } from '../constants';
import { useFunnelChartData, type FunnelChartRow } from '../hooks/useFunnelChartData';
import FunnelChartHoverPopup from './FunnelChartHoverPopup';
import FunnelChartLegend from './FunnelChartLegend';

interface AnalysisFunnelChartProps {
  /** APIレスポンスのサマリーデータ（API ステップキー単位、合計値） */
  summary: AgentApiAnalysisSharedSummaryItem[];
  /** APIレスポンスのチャートデータ（系列ごとの値配列） */
  chart: AgentApiAnalysisSharedChart;
  /** GroupBy 指定有無 */
  isGrouped: boolean;
  /** 表示中のステップキー（APIキー配列） */
  visibleSteps: string[];
  /** メトリクス種別 */
  metric: 'uu' | 'count';
  /**
   * 系列の正規順（フィルター一覧の並び＝organization_users の id 等）。凡例・積み上げをこの順に揃える。
   * グループ無し（単色バー）では不要なため任意。未指定時は key 安定ソートにフォールバックする。
   */
  seriesOrderKeys?: string[];
}

// バーの色（Semantic/Primary）
const BAR_COLOR = '#005fff';
// 合計値ラベルの色
const VALUE_COLOR_UP = '#005fff'; // delta >= 0（Semantic/Primary）
const VALUE_COLOR_DOWN = '#fa0000'; // delta < 0（Semantic/Accent1）

interface HoverState {
  x: number;
  y: number;
  label: string;
  value: number;
}

/**
 * series を決定的な順序に並べ替える。
 * 並び順はフィルター一覧（organization_users 等）の正規順 `orderKeys` に合わせる。
 * これにより、凡例・積み上げ・フィルターのプルダウンで担当者の並びが一致する。
 * - orderKeys に含まれるキーはその順序
 * - 含まれないキー（解約済ユーザー等）は後方に key で安定ソート
 * - OTHERS は常に末尾
 * orderKeys が空でも key 安定ソート＋OTHERS末尾となり、再フェッチでの色ズレを防ぐ。
 */
function sortSeriesByOrder(
  series: AgentApiAnalysisSharedChartSeries[],
  orderKeys: string[]
): AgentApiAnalysisSharedChartSeries[] {
  const orderIndex = new Map(orderKeys.map((k, i) => [k, i]));
  const rank = (key: string) =>
    orderIndex.has(key) ? (orderIndex.get(key) as number) : Number.POSITIVE_INFINITY;
  return [...series].sort((a, b) => {
    const aOthers = a.key === OTHERS_GROUP_KEY;
    const bOthers = b.key === OTHERS_GROUP_KEY;
    if (aOthers !== bOthers) return aOthers ? 1 : -1;
    const ra = rank(a.key);
    const rb = rank(b.key);
    if (ra !== rb) return ra - rb;
    if (a.key < b.key) return -1;
    if (a.key > b.key) return 1;
    return 0;
  });
}

/** 上辺2角のみ半径 r で角丸にした矩形パス（積み上げ最上段用） */
function topRoundedRectPath(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w / 2, h));
  return [
    `M${x},${y + h}`,
    `L${x},${y + rr}`,
    `Q${x},${y} ${x + rr},${y}`,
    `L${x + w - rr},${y}`,
    `Q${x + w},${y} ${x + w},${y + rr}`,
    `L${x + w},${y + h}`,
    'Z',
  ].join(' ');
}

/** 角丸なしの矩形パス（中間・最下段セグメント用） */
function flatRectPath(x: number, y: number, w: number, h: number): string {
  return `M${x},${y} h${w} v${h} h${-w} Z`;
}

/**
 * Recharts の <Customized> 用に、バー上端の合計ラベルを X/Y スケールから直接描画する。
 * - 表示値・Y座標は seriesSum（バーの実視覚高さに一致）を使う
 *   GroupBy 時は担当者の N:M 重複により seriesSum > summary.total になり得るため、
 *   summary.total を使うとラベル位置がバー上端からずれる
 * - 色分けは summary 由来の delta（前期比）に基づく（青: 増加・同 / 赤: 減少）
 */
function buildTotalLabelsRenderer(data: FunnelChartRow[]) {
  const TotalLabelsRenderer = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    props: any
  ) => {
    const xAxisMap = props?.xAxisMap as Record<string, unknown> | undefined;
    const yAxisMap = props?.yAxisMap as Record<string, unknown> | undefined;
    if (!xAxisMap || !yAxisMap) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const xAxis = Object.values(xAxisMap)[0] as any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const yAxis = Object.values(yAxisMap)[0] as any;
    if (!xAxis?.scale || !yAxis?.scale) return null;
    const bandwidth =
      typeof xAxis.scale.bandwidth === 'function' ? Number(xAxis.scale.bandwidth()) : 0;

    return (
      <g>
        {data.map((row, idx) => {
          if (row.seriesSum === 0) return null;
          const xStart = xAxis.scale(row.step);
          const yTop = yAxis.scale(row.seriesSum);
          if (typeof xStart !== 'number' || typeof yTop !== 'number') return null;
          const color = row.delta < 0 ? VALUE_COLOR_DOWN : VALUE_COLOR_UP;
          return (
            <text
              key={`${row.step}-${idx}`}
              x={xStart + bandwidth / 2}
              y={yTop - 6}
              textAnchor="middle"
              fontSize={14}
              fontWeight={700}
              fill={color}
            >
              {row.seriesSum.toLocaleString()}
            </text>
          );
        })}
      </g>
    );
  };
  TotalLabelsRenderer.displayName = 'AnalysisFunnelChartTotalLabels';
  return TotalLabelsRenderer;
}

/**
 * 分析ファネルチャート（Figma 準拠）
 * - 単色モード（isGrouped=false）: summary の current_value を縦棒で描画、delta で値ラベル色分け
 * - グループモード（isGrouped=true）: chart.series を積み上げ縦棒で描画、上端に合計値ラベル
 *   - 凡例はチャート右側に縦並び（max-height + overflow-y-auto でスクロール）
 *   - ホバー時は対象セグメントのみ「グループ名: 値」をフローティング表示
 * - 合計ラベルはどちらのモードでも <Customized> で X/Y スケールから直接描画する
 */
export default function AnalysisFunnelChart({
  summary,
  chart,
  isGrouped,
  visibleSteps,
  metric,
  seriesOrderKeys = [],
}: AnalysisFunnelChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<HoverState | null>(null);

  const data = useFunnelChartData(summary, chart, visibleSteps);
  const unit = metric === 'uu' ? '名' : '件';
  const renderTotalLabels = buildTotalLabelsRenderer(data);

  // 正規順（フィルター一覧の並び）に揃えた series。色もこの順の index で決定する。
  const canonicalSeries = useMemo(
    () => sortSeriesByOrder(chart.series, seriesOrderKeys),
    [chart.series, seriesOrderKeys]
  );
  // key → 固定色（正規順 index 由来）。宣言順を変えても色が動かないようにマップで保持する。
  const colorByKey = useMemo(
    () => new Map(canonicalSeries.map((s, idx) => [s.key, colorForSeries(s.key, idx)])),
    [canonicalSeries]
  );

  // 凡例は正規順（フィルター一覧と同じ並び）で上から表示する。
  const legendItems = useMemo(
    () =>
      canonicalSeries.map((s) => ({
        key: s.key,
        label: s.label,
        color: colorByKey.get(s.key) ?? colorForSeries(s.key, 0),
      })),
    [canonicalSeries, colorByKey]
  );

  // 積み上げは BarChart の reverseStackOrder で反転し、正規順の先頭（自分のアカウント等）を
  // 最上段にする。Bar は正規順のまま宣言する（key 安定のため JSX 側の reverse は recharts の
  // 内部登録順に反映されず効かない。reverseStackOrder はセレクタ側で確実に反転される）。

  // セグメントホバー（グループモード）：active series 情報をコンテナ相対座標で記録
  const handleSegmentEnter =
    (label: string, valueKey: string) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (barData: any, _index: number, e: ReactMouseEvent<SVGElement>) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setHover({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        label,
        value: Number(barData?.[valueKey] ?? 0),
      });
    };

  return (
    <div className="flex w-full gap-4">
      {/* チャート本体 */}
      <div
        ref={containerRef}
        className="relative min-w-0 flex-1 [&_.recharts-surface:focus:not(:focus-visible)]:outline-hidden [&_.recharts-wrapper_*:focus:not(:focus-visible)]:outline-hidden [&_.recharts-wrapper:focus:not(:focus-visible)]:outline-hidden"
      >
        <ResponsiveContainer width="100%" height={358}>
          <BarChart
            data={data}
            margin={{ top: 24, right: 16, left: 0, bottom: 0 }}
            // 正規順（凡例・フィルターと同じ）で宣言した系列の先頭を最上段にするため積み上げ順を反転する。
            reverseStackOrder
          >
            <CartesianGrid stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="step"
              tick={{ fontSize: 12, fill: '#6d6d6d' }}
              axisLine={false}
              tickLine={false}
              interval={0}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#88898b' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => v.toLocaleString()}
              allowDecimals={false}
            />
            {/* 単色モードのみ Recharts 標準 Tooltip を使用（カスタム1行表示） */}
            {!isGrouped && (
              <Tooltip
                cursor={{ fill: 'rgba(0, 95, 255, 0.05)' }}
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const value = Number(payload[0]?.value ?? 0);
                  return (
                    <div
                      role="tooltip"
                      aria-live="polite"
                      style={{
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 4,
                        boxShadow: '0px 0px 4px 0px rgba(0,0,0,0.08)',
                        fontSize: 12,
                        padding: '6px 10px',
                        color: '#262626',
                      }}
                    >
                      {label}: {value.toLocaleString()}
                      {unit}
                    </div>
                  );
                }}
              />
            )}
            {/* バー本体 */}
            {!isGrouped ? (
              <Bar dataKey="total" fill={BAR_COLOR} radius={[4, 4, 0, 0]} maxBarSize={50} />
            ) : (
              canonicalSeries.map((series, idx) => {
                const color = colorByKey.get(series.key) ?? colorForSeries(series.key, idx);
                return (
                  <Bar
                    key={series.key}
                    dataKey={series.key}
                    name={series.label}
                    stackId="stack"
                    fill={color}
                    maxBarSize={50}
                    isAnimationActive={false}
                    // 実際に見えている最上段セグメントだけ上部を角丸にする（自前パスで確実に描画）。
                    // 積み上げの上端（value[1]）が合計(seriesSum)に達したセグメント＝最上段、で判定。
                    // recharts の積み上げ順に依存しないため、宣言順を変えても誤判定しない。
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    shape={(shapeProps: any) => {
                      const { x, y, width, height, value } = shapeProps ?? {};
                      if (!width || !height) return null;
                      const row = (shapeProps?.payload ?? shapeProps) as Record<string, unknown>;
                      const total = Number(row?.seriesSum ?? 0);
                      const segEnd = Array.isArray(value) ? Number(value[1]) : Number(value);
                      const isTop = total > 0 && segEnd >= total;
                      const d = isTop
                        ? topRoundedRectPath(x, y, width, height, 4)
                        : flatRectPath(x, y, width, height);
                      return (
                        <path
                          d={d}
                          fill={color}
                          onMouseEnter={(e: ReactMouseEvent<SVGElement>) =>
                            handleSegmentEnter(series.label, series.key)(row, idx, e)
                          }
                          onMouseLeave={() => setHover(null)}
                        />
                      );
                    }}
                  />
                );
              })
            )}
            {/* 合計ラベル（X/Y スケールから直接描画。LabelList の 0 値ガード問題を回避） */}
            <Customized component={renderTotalLabels} />
          </BarChart>
        </ResponsiveContainer>
        {/* グループモード：セグメントホバー時のフローティングポップ */}
        {isGrouped && hover && (
          <FunnelChartHoverPopup
            x={hover.x}
            y={hover.y}
            label={hover.label}
            value={hover.value}
            unit={unit}
          />
        )}
      </div>
      {/* グループモード：右側スクロール凡例（バーと同じ安定順序で色を一致させる） */}
      {isGrouped && <FunnelChartLegend items={legendItems} />}
    </div>
  );
}
